/**
 * Window helpers.
 *
 * The platform stores `scheduledWindowStart`/`scheduledWindowEnd` as the DELIVERY
 * window (= the chosen pickup window + driveTime + service time). Drivers should only ever
 * see the PICKUP window (when to be at the seller), so we subtract those to derive it.
 * Storage (Store-to-Sell) jobs use driveTimeMinutes=0, so pickup === scheduled there.
 */
import { pairPickupWithDelivery } from './pickupWindow';
// scheduledWindowStart/End is the DELIVERY (arrival) window = pickup + PICKUP-PHASE work +
// driveTime. The pickup phase is HALF the full service time (service = disassembly-at-pickup +
// reassembly-at-buyer; reassembly happens AFTER arrival, so only the pickup half is in the
// shift). To recover the pickup window we subtract driveTime + serviceTime/2, rounded to 5 to
// match the backend. serviceTimeMinutes is attached by the backend; absent → treated as 0.
function offsetMinutes(job: any): number {
  // TWO-DAY jobs (>600 road miles) are not pickup + drive: the delivery window is the same
  // hours the NEXT day, so the gap between the two is a day, not a drive. Subtracting the
  // drive time here would put the pickup at 9 PM the evening before.
  if (job?.isTwoDay) return 24 * 60;
  const pickupPhase = Math.round((Number(job?.serviceTimeMinutes) || 0) / 2 / 5) * 5;
  return (Number(job?.driveTimeMinutes) || 0) + pickupPhase;
}

export function pickupWindowStart(job: any): Date | null {
  if (!job?.scheduledWindowStart) return null;
  return new Date(new Date(job.scheduledWindowStart).getTime() - offsetMinutes(job) * 60000);
}

export function pickupWindowEnd(job: any): Date | null {
  if (!job?.scheduledWindowEnd) return null;
  return new Date(new Date(job.scheduledWindowEnd).getTime() - offsetMinutes(job) * 60000);
}

/** Millis of the pickup window start (for sorting); Infinity when unscheduled. */
export function pickupWindowSortKey(job: any): number {
  const d = pickupWindowStart(job);
  return d ? d.getTime() : Infinity;
}

// ── Driver arrival buffer ──────────────────────────────────────────────────────
// Drivers tend to run late and need setup/service time, so we show them the pickup
// window shifted EARLIER: 30 min earlier at the start (arrive with a cushion) and 1 hr
// earlier at the end (be wrapping up well before the seller's window closes). The
// SELLER's real window is unchanged — this is display-only on the driver side. Admin
// shows both the seller window and this buffered "driver shown" window.
export const DRIVER_BUFFER_START_MIN = 30;
export const DRIVER_BUFFER_END_MIN = 60;

export function driverWindowStart(job: any): Date | null {
  // The backend computes the authoritative window from the seller's actual pickupWindows
  // (correct even on jobs with service/disassembly time). Prefer it; the scheduled−driveTime
  // math is only a fallback for older cached jobs.
  if (job?.driverWindowStart) return new Date(job.driverWindowStart);
  const s = pickupWindowStart(job);
  return s ? new Date(s.getTime() - DRIVER_BUFFER_START_MIN * 60000) : null;
}

export function driverWindowEnd(job: any): Date | null {
  if (job?.driverWindowStart) return job.driverWindowEnd ? new Date(job.driverWindowEnd) : null;
  const s = pickupWindowStart(job);
  const e = pickupWindowEnd(job);
  if (!e) return null;
  const ds = s ? new Date(s.getTime() - DRIVER_BUFFER_START_MIN * 60000) : null;
  let de = new Date(e.getTime() - DRIVER_BUFFER_END_MIN * 60000);
  if (ds && de.getTime() <= ds.getTime()) de = new Date(ds.getTime() + 30 * 60000); // never invert; min 30-min window
  return de;
}

/** Apply the driver arrival buffer to a RAW pickup window (start/end Dates or ISO strings).
 *  Use this for any pickup window the driver sees (board options, job detail, the window
 *  picker) so they always see the buffered times. Selection is keyed by window id, not the
 *  displayed time, so buffering the display is safe. */
export function bufferedPickup(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
): { start: Date | null; end: Date | null } {
  const s = start ? new Date(new Date(start).getTime() - DRIVER_BUFFER_START_MIN * 60000) : null;
  let e = end ? new Date(new Date(end).getTime() - DRIVER_BUFFER_END_MIN * 60000) : null;
  if (s && e && e.getTime() <= s.getTime()) e = new Date(s.getTime() + 30 * 60000);
  return { start: s, end: e };
}

/** The list of BUFFERED pickup windows a driver can see for a job BEFORE accepting — used on
 *  the job board card + job detail page so drivers can compare availability without claiming.
 *  Each entry's id is the deliveryWindowId (what accept/select-window keys on), and the times
 *  are the seller's offered windows with the driver arrival buffer applied. */
export function bufferedPickupWindowList(job: any): { id: string; day: string; time: string; deliveryDay?: string; deliveryTime?: string }[] {
  const paired = pairPickupWithDelivery(job?.pickupWindows, job?.deliveryWindows, job?.scheduledWindowStart);
  const tz: Intl.DateTimeFormatOptions = job?.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
  // Delivery times are shown ONLY on two-day jobs, and in the BUYER's timezone — on a
  // same-day job the delivery window is an internal number the driver has no use for.
  const dtz: Intl.DateTimeFormatOptions = job?.deliveryTimezone ? { timeZone: job.deliveryTimezone } : {};
  const out: { id: string; day: string; time: string; deliveryDay?: string; deliveryTime?: string }[] = [];
  for (const p of paired) {
    const bp = bufferedPickup(p.pickupStart, p.pickupEnd);
    if (!bp.start) continue;
    const day = bp.start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tz });
    const time = `${bp.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tz })}${bp.end ? ` – ${bp.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tz })}` : ''}`;
    const row: { id: string; day: string; time: string; deliveryDay?: string; deliveryTime?: string } = { id: p.deliveryWindowId, day, time };
    if (job?.isTwoDay) {
      const ds = new Date(p.deliveryStartTime);
      const de = new Date(p.deliveryEndTime);
      row.deliveryDay = ds.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...dtz });
      row.deliveryTime = `${ds.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...dtz })} – ${de.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...dtz })}`;
    }
    out.push(row);
  }
  return out;
}

/** The DELIVERY window a two-day job is locked to, in the buyer's timezone. Null on any
 *  same-day job — drivers see pickup windows only, which is the whole rule this excepts. */
export function fmtTwoDayDelivery(job: any): string | null {
  if (!job?.isTwoDay || !job?.scheduledWindowStart) return null;
  const dtz: Intl.DateTimeFormatOptions = job?.deliveryTimezone ? { timeZone: job.deliveryTimezone } : {};
  const s = new Date(job.scheduledWindowStart);
  const e = job.scheduledWindowEnd ? new Date(job.scheduledWindowEnd) : null;
  const t = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...dtz });
  return `${s.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...dtz })} · ${t(s)}${e ? ` – ${t(e)}` : ''}`;
}

/** Format the driver-facing (buffered) pickup window for a job, e.g. "Mon, Jun 9 · 7:30 – 10:00 AM". */
export function fmtDriverWindow(job: any, withDate = true): string | null {
  const s = driverWindowStart(job);
  if (!s) return null;
  const e = driverWindowEnd(job);
  const tz = job?.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
  const datePart = withDate ? `${s.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tz })} · ` : '';
  const t = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tz });
  return `${datePart}${t(s)}${e ? ` – ${t(e)}` : ''}`;
}
